import { Select } from 'antd';
import type { VehicleTypeDto } from '@pams/shared';
import { withChosenOptions } from '@/lib/selectOptions';

/**
 * The vehicle block.
 *
 * ⚠️ DELIBERATELY NOT REORDERABLE, and that asymmetry with the post picker is the point.
 *
 * Per-policy vehicle order has no meaning: the block is ordered by the MASTER's
 * `displayOrder`, which is what `lib/policyPreview.ts` renders. Giving this a reorder
 * affordance would imply an ordering that nothing reads, and the first person to notice
 * would "fix" the preview instead of the control.
 */
export const VehicleTypeField = ({
  value,
  onChange,
  vehicleTypes,
  resolve,
  disabled,
}: {
  value?: string[];
  onChange?: (value: string[]) => void;
  vehicleTypes: readonly VehicleTypeDto[];
  /**
   * ⚠️ [D4.5 §1b] WHAT AN ALREADY-CHOSEN id RENDERS AS. `GET /vehicle-types` returns CURRENT
   * rows only, so a draft naming an archived type had a value its options never contained —
   * and rc-select falls through to `label ?? item.value`, putting an ObjectId on a tag
   * directly beneath the banner that WARNS about archived columns. The page merges the
   * master with the saved version's embedded summaries to build this (`knownVehicleTypes`).
   */
  resolve?: (id: string) => { name: string } | undefined;
  disabled?: boolean;
}) => (
  <Select
    mode="multiple"
    value={value ?? []}
    onChange={(next: string[]) => onChange?.(next)}
    disabled={disabled}
    showSearch
    optionFilterProp="label"
    placeholder="Which vehicle types apply?"
    className="w-full"
    data-testid="vehicle-type-picker"
    options={withChosenOptions(
      vehicleTypes.map((vehicleType) => ({
        value: vehicleType.id,
        // The wildcard says so, read off the FLAG rather than off its code or name.
        label: vehicleType.isWildcard ? `${vehicleType.name} (any type satisfies it)` : vehicleType.name,
      })),
      value ?? [],
      resolve,
    )}
  />
);
